import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import {
  filterSentryEvents,
  httpClientIntegration,
  initLogging,
  logError,
  thirdPartyErrorFilterIntegration,
} from '@betty/toolkit/logging';
import { ApiError, ValidationError, initApi } from '@betty/toolkit/api';
import { Config } from '@betty/toolkit/config';
import { SecureStorage } from '@betty/toolkit/storage';
import {
  refreshToken,
  getRefreshTokenPromise,
  logout,
} from '@betty/auth/state';
import { getDeviceFingerprint, getDeviceId, getSessionId } from './src/utils';
import packageJson from './package.json';

import './src/assets';
export * from './src/assets';
import App from './src/App';

import '@betty/auth/state';
import '@betty/account/state';
import '@betty/account/navigation';
import '@betty/games/state';
import '@betty/wallet/state';
import '@betty/rewards/state';
import '@betty/status/state';
import '@betty/levels/state';
import '@betty/chat/state';
import '@betty/smartico/state';
import '@betty/onboarding';
import '@betty/inbox';
import '@betty/shop';
import '@betty/responsible-gaming';

import './src/state';

import { store } from '@betty/toolkit/store';
import { isRejectedWithValue } from '@betty/toolkit/state';
import { isPaymentResultIframe } from '@betty/toolkit/utils';
import { Toast } from '@betty/moonlight';

if (isPaymentResultIframe()) {
  window.top?.postMessage(
    JSON.stringify({
      type: 'PAYMENT_RESULT',
      location: `${window.location.pathname}${window.location.search}`,
    }),
  );
} else {
  initLogging({
    enabled: !__DEV__,
    dsn: Config.SENTRY_DSN,
    environment: Config.ENVIRONMENT,
    release: packageJson.version,
    dist: 'web',
    maxValueLength: 1024,
    ignoreErrors: [
      'TypeError: Failed to fetch',
      'TypeError: Load failed',
      'Load failed',
      'TypeError: NetworkError when attempting to fetch resource.',
    ],
    beforeSend: filterSentryEvents,
    integrations: [httpClientIntegration, thirdPartyErrorFilterIntegration],
  });

  initApi({
    getBaseUrl: () => Config.API_URL,
    getSocketUrl: () => `${Config.API_URL}/notifications/hubs/generic`,
    getUserId: async () => {
      const id = await SecureStorage.getItem('user_id');

      return id;
    },
    getAccessToken: async () => {
      const token = await SecureStorage.getItem('access_token');

      return token;
    },
    refreshToken: async () => {
      const result = await store.dispatch(refreshToken());

      if (refreshToken.fulfilled.match(result)) {
        return result.payload.access_token;
      }

      if (isRejectedWithValue(result) && result.payload?.status) {
        throw new ApiError('Unable to refresh token', result.payload);
      }

      throw new Error('Unable to refresh token unknown error');
    },
    waitForRefreshToken: async (url) => {
      const refreshTokenPromise = getRefreshTokenPromise();

      if (refreshTokenPromise && url.indexOf('/connect/token') === -1) {
        await refreshTokenPromise;
      }
    },
    getToken: async () => {
      const tokenString = await SecureStorage.getItem('token');

      if (tokenString) {
        const token = JSON.parse(tokenString);

        return token;
      }
    },
    getRequestHeaders: async () => {
      const deviceId = await getDeviceId();
      const sessionId = getSessionId();
      const deviceFingerprint = getDeviceFingerprint();

      return {
        'x-device-metadata': deviceFingerprint,
        'x-fe-session-id': sessionId,
        'x-fe-device-id': deviceId,
        'x-channel-name': 'web',
      };
    },
    onApiError: (error) => {
      if (error instanceof ApiError && error.status >= 500) {
        if (
          error.status === 503 &&
          error.data?.errorCode === 'system-under-maintenance'
        ) {
          store.dispatch(logout(false));

          Toast.show({
            type: 'error',
            title: error.data?.title,
            text: error.data.message,
          });
        }

        logError('API 500 Error', error);
      } else if (error instanceof ValidationError) {
        logError('Response Validation Error', error.data);
      }
    },
  });

  const container = document.getElementById('betty-root') || document.body;
  const root = createRoot(container);

  root.render(createElement(App));

  const spinner = document.getElementById('spinner');

  if (spinner) {
    document.body.removeChild(spinner);
  }
}
